import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Body2, List, ListItem, ListItemText, Card } from '@dhl-ui/components'
import { AgreedPlace, Home, ServicePoint } from '@dhl/icons'
import type { AddressInterventionPreference } from '@dhl/receiver-preference-api'
import type { ProfileAddress } from '@dhl/receiver-profile-api'
import { type SvgIconProps } from '@dhl/theme'

type Props = {
  firstName?: string
  lastName?: string
  preference?: AddressInterventionPreference
  address?: ProfileAddress
  primaryEmailAddress?: string
  additionalEmailAddress?: string
  mobileNumber?: string
  notificationPreferences: {
    emailNotificationsEnabled: boolean
    pushNotificationsForIncomingParcelsEnabled: boolean
    pushNotificationsForOutgoingParcelsEnabled: boolean
  }
}

type NameValuePair = {
  title: string
  value?: string | undefined | React.ReactNode
}

type DeliveryPreferenceProps = {
  name: string
  icon: React.ElementType<SvgIconProps>
}

const DeliveryPreference: FC<DeliveryPreferenceProps> = ({ icon: IconComponent, name }) => (
  <Box display="flex">
    <IconComponent sx={{ height: 16, width: 16 }} />
    <Box component="span" ml={0.5}>
      {name}
    </Box>
  </Box>
)

export const ProfileSummary: FC<Props> = ({
  additionalEmailAddress,
  address,
  firstName,
  lastName,
  mobileNumber,
  notificationPreferences: {
    emailNotificationsEnabled,
    pushNotificationsForIncomingParcelsEnabled,
    pushNotificationsForOutgoingParcelsEnabled,
  },
  preference,
  primaryEmailAddress,
}) => {
  const { t } = useTranslation()

  const getFormattedAddress = (profileAddress?: ProfileAddress) => {
    if (!profileAddress) {
      return undefined
    }

    const { address: addressToFormat } = profileAddress

    return `${addressToFormat.street} ${addressToFormat.houseNumber}${addressToFormat.houseNumberSuffix}, ${addressToFormat.postalCode} ${addressToFormat.city}`
  }

  const getFormattedDeliveryPreference = (preferenceValue?: AddressInterventionPreference) => {
    if (!preferenceValue) {
      return undefined
    }

    const { servicePoint, agreedPlace } = preferenceValue

    if (servicePoint?.name && servicePoint?.active) {
      return <DeliveryPreference icon={ServicePoint} name={servicePoint?.name} />
    }

    if (agreedPlace?.active) {
      return (
        <DeliveryPreference
          icon={AgreedPlace}
          name={`${
            agreedPlace?.ringDoorbell
              ? t('portal.preferences.agreedPlace.cardTextWithRingDoorbell')
              : t('portal.preferences.agreedPlace.cardTextWithoutRingDoorbell')
          } ${agreedPlace.description}`}
        />
      )
    }

    return (
      <DeliveryPreference
        icon={Home}
        name={
          agreedPlace.ringDoorbell === null
            ? t('portal.preferences.noPreferences.cardText')
            : t('portal.preferences.neighbours.ringDoorbellDescription')
        }
      />
    )
  }

  const getFormattedNotificationPreferences = () => {
    const items: string[] = []

    if (pushNotificationsForIncomingParcelsEnabled) {
      items.push(
        `${t('portal.profileSetup.notificationPreference.pushNotificationsCardTitle')}: ${t('portal.profileSetup.notificationPreference.pushNotificationIncomingParcelsLabel')}`
      )
    }
    if (pushNotificationsForOutgoingParcelsEnabled) {
      items.push(
        `${t('portal.profileSetup.notificationPreference.pushNotificationsCardTitle')}: ${t('portal.profileSetup.notificationPreference.pushNotificationOutgoingParcelsLabel')}`
      )
    }
    if (emailNotificationsEnabled) {
      items.push(
        `${t('portal.profileSetup.notificationPreference.emailNotificationCardTitle')}: ${t('portal.profileSetup.notificationPreference.emailNotificationLabel')}`
      )
    }

    if (items.length === 0) {
      return undefined
    }

    return (
      <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'disc' }}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </Box>
    )
  }

  const getProfileData: () => NameValuePair[] = () => {
    const emailAddresses = [primaryEmailAddress, additionalEmailAddress].filter(Boolean).join(', ')
    const name = [firstName, lastName].filter(Boolean).join(' ')

    return [
      { title: t('portal.profileSetup.done.labels.name'), value: name },
      { title: t('portal.profileSetup.done.labels.address'), value: getFormattedAddress(address) },
      {
        title: t('portal.profileSetup.done.labels.deliveryPreference'),
        value: getFormattedDeliveryPreference(preference),
      },
      { title: t('portal.profileSetup.done.labels.emailAddresses'), value: emailAddresses },
      { title: t('portal.profileSetup.done.labels.phoneNumber'), value: mobileNumber },
      { title: t('portal.profileSetup.done.labels.notifications'), value: getFormattedNotificationPreferences() },
    ].filter(({ value }) => !!value)
  }

  const profileData = getProfileData()

  if (!profileData.length) {
    return null
  }

  return (
    <Card withPaddingTop>
      <Card.Header title={t('portal.profileSetup.done.cardTitle')} />
      <Card.Content noPaddingBottom noPaddingSides noPaddingTop>
        <List>
          {profileData.map(({ title, value }, index) => (
            // eslint-disable-next-line react/no-array-index-key -- we don't have a unique key available
            <ListItem hasDivider={index !== profileData.length - 1} key={index}>
              <ListItemText emphasis primary={title}>
                <Body2 sx={{ pt: 0.25 }}>{value}</Body2>
              </ListItemText>
            </ListItem>
          ))}
        </List>
      </Card.Content>
    </Card>
  )
}
